import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

import { fmtDate, fmtDateTime, timeAgo, daysFromNowCST } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
const money = (v) => v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';
const fmt = fmtDate;

const STATUS_COLORS = {
  invited: '#3b82f6', reviewing_documents: '#6366f1', takeoff_in_progress: '#f59e0b',
  pricing_in_progress: '#f97316', bid_ready: '#8b5cf6', bid_submitted: '#14b8a6',
  clarification: '#ec4899', negotiation: '#a855f7', awaiting_award: '#06b6d4',
  awarded: '#16a34a', lost: '#6b7280', no_bid: '#9ca3af', cancelled: '#9ca3af',
};
const STATUS_LABELS = {
  invited: 'Invited', reviewing_documents: 'Reviewing Docs', takeoff_in_progress: 'Takeoff',
  pricing_in_progress: 'Pricing', bid_ready: 'Bid Ready', bid_submitted: 'Submitted',
  clarification: 'Clarification', negotiation: 'Negotiation', awaiting_award: 'Awaiting Award',
  awarded: 'Awarded', lost: 'Lost', no_bid: 'No Bid', cancelled: 'Cancelled',
};

export { STATUS_COLORS, STATUS_LABELS };

export default function BidInvitations({ navigate }) {
  const isMobile = useIsMobile();
  const [bids, setBids] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    account_id: '', project_name: '', project_number: '', bid_due_date: '',
    bid_due_time: '', scope_description: '', prevailing_wage: false, bond_required: false, notes: '',
  });

  useEffect(() => {
    loadAll();
  }, [filter, search]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter && filter !== 'active' && filter !== 'all') params.append('bid_status', filter);
      if (search) params.append('search', search);
      const [bidsRes, dashRes] = await Promise.all([
        fetch(`${API}/api/sales/commercial/bids/list?${params}`, authOpts()),
        fetch(`${API}/api/sales/commercial/bids/dashboard`, authOpts()),
      ]);
      if (bidsRes.ok) {
        let data = await bidsRes.json();
        if (filter === 'active') {
          data = data.filter(b => !['awarded', 'lost', 'no_bid', 'cancelled'].includes(b.bid_status));
        }
        setBids(data);
      }
      if (dashRes.ok) setDashboard(await dashRes.json());
    } finally { setLoading(false); }
  };

  const loadAccounts = async () => {
    const res = await fetch(`${API}/api/sales/commercial/accounts`, authOpts());
    if (res.ok) setAccounts(await res.json());
  };

  const handleCreate = async () => {
    if (!form.account_id || !form.project_name.trim()) return;
    const res = await fetch(`${API}/api/sales/commercial/bids`, authOpts({
      method: 'POST', body: JSON.stringify(form),
    }));
    if (res.ok) {
      const bid = await res.json();
      setShowCreate(false);
      setForm({ account_id: '', project_name: '', project_number: '', bid_due_date: '', bid_due_time: '', scope_description: '', prevailing_wage: false, bond_required: false, notes: '' });
      navigate(`/sales/bids/${bid.id}`);
    }
  };

  const daysUntilDue = (d) => daysFromNowCST(d);

  const kpiStyle = { background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: isMobile ? '12px' : '16px 20px', textAlign: 'center' };
  const kpiValue = { fontSize: isMobile ? 22 : 28, fontWeight: 800, color: '#1e293b', margin: 0 };
  const kpiLabel = { fontSize: 11, color: '#64748b', margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: '0.5px' };

  return (
    <div data-testid="bid-invitations" style={{ padding: isMobile ? '14px 10px' : '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 20, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 0 }}>
        <div>
          <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: '#1e293b', margin: 0 }}>Bid Invitations</h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Commercial bid management and tracking</p>
        </div>
        <button data-testid="create-bid-btn" onClick={() => { setShowCreate(true); loadAccounts(); }}
          style={{ padding: '10px 18px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
          + New Bid Invitation
        </button>
      </div>

      {/* KPI Cards */}
      {dashboard && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)', gap: isMobile ? 8 : 12, marginBottom: 20 }}>
          <div style={kpiStyle}><p style={kpiValue}>{dashboard.active_bids}</p><p style={kpiLabel}>Active Bids</p></div>
          <div style={kpiStyle}><p style={{ ...kpiValue, color: dashboard.due_this_week > 0 ? '#f59e0b' : '#1e293b' }}>{dashboard.due_this_week}</p><p style={kpiLabel}>Due This Week</p></div>
          <div style={kpiStyle}><p style={{ ...kpiValue, color: dashboard.overdue > 0 ? '#ef4444' : '#1e293b' }}>{dashboard.overdue}</p><p style={kpiLabel}>Overdue</p></div>
          <div style={kpiStyle}><p style={kpiValue}>{dashboard.win_rate}%</p><p style={kpiLabel}>Win Rate</p></div>
          <div style={{ ...kpiStyle, ...(isMobile ? { gridColumn: 'span 2' } : {}) }}><p style={kpiValue}>{money(dashboard.total_awarded)}</p><p style={kpiLabel}>Awarded Revenue</p></div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {['active', 'all', 'invited', 'reviewing_documents', 'takeoff_in_progress', 'bid_submitted', 'awaiting_award', 'awarded', 'lost'].map(f => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            style={{
              padding: '6px 12px', border: '1px solid', borderRadius: 6, fontSize: 12, fontWeight: filter === f ? 600 : 400,
              cursor: 'pointer', minHeight: 36,
              background: filter === f ? '#1e293b' : '#fff',
              color: filter === f ? '#fff' : '#475569',
              borderColor: filter === f ? '#1e293b' : '#e2e8f0',
            }}>
            {f === 'active' ? 'Active' : f === 'all' ? 'All' : STATUS_LABELS[f] || f}
          </button>
        ))}
        <input data-testid="bid-search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search bids..." style={{ flex: 1, minWidth: 160, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 36 }} />
      </div>

      {/* Bid List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading bids...</div>
      ) : bids.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No bids found</p>
          <p style={{ fontSize: 13 }}>Create a new bid invitation to get started</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {bids.map(bid => {
            const days = daysUntilDue(bid.bid_due_date);
            const isUrgent = days !== null && days <= 3 && days >= 0;
            const isOverdue = days !== null && days < 0;
            return (
              <div key={bid.id} data-testid={`bid-card-${bid.id}`}
                onClick={() => navigate(`/sales/bids/${bid.id}`)}
                style={{
                  background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: isMobile ? '14px' : '16px 20px',
                  cursor: 'pointer', transition: 'all .15s',
                  borderLeft: `4px solid ${STATUS_COLORS[bid.bid_status] || '#94a3b8'}`,
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 8 : 0 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: isMobile ? '100%' : 400 }}>
                        {bid.project_name || 'Untitled Bid'}
                      </h3>
                      <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 99, background: `${STATUS_COLORS[bid.bid_status]}18`, color: STATUS_COLORS[bid.bid_status], fontWeight: 600 }}>
                        {STATUS_LABELS[bid.bid_status] || bid.bid_status}
                      </span>
                    </div>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                      {bid.company_name}{bid.project_number ? ` — #${bid.project_number}` : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: isMobile ? 12 : 20, alignItems: 'center', flexWrap: 'wrap' }}>
                    {bid.bid_due_date && (
                      <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                        <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>Due</p>
                        <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: isOverdue ? '#ef4444' : isUrgent ? '#f59e0b' : '#1e293b' }}>
                          {fmt(bid.bid_due_date)}{bid.bid_due_time ? ` ${bid.bid_due_time}` : ''}
                        </p>
                        {isOverdue && <p style={{ fontSize: 10, color: '#ef4444', margin: 0, fontWeight: 600 }}>OVERDUE</p>}
                        {isUrgent && !isOverdue && <p style={{ fontSize: 10, color: '#f59e0b', margin: 0, fontWeight: 600 }}>{days === 0 ? 'DUE TODAY' : `${days}d left`}</p>}
                      </div>
                    )}
                    {bid.submitted_amount && (
                      <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                        <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>Submitted</p>
                        <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: '#1e293b' }}>{money(bid.submitted_amount)}</p>
                      </div>
                    )}
                    <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                      <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>Docs</p>
                      <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: '#1e293b' }}>{bid.documents_count || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Bid Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div data-testid="create-bid-modal" style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', padding: isMobile ? '20px 16px' : '24px 28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#1e293b' }}>New Bid Invitation</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Account *</label>
                <select data-testid="bid-account-select" value={form.account_id} onChange={e => setForm(f => ({ ...f, account_id: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44 }}>
                  <option value="">Select account...</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.company_name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Project Name *</label>
                <input data-testid="bid-project-name" value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))}
                  placeholder="e.g., Denton Towne Center — Pipe Fence" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Project #</label>
                  <input value={form.project_number} onChange={e => setForm(f => ({ ...f, project_number: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Bid Due Date</label>
                  <input data-testid="bid-due-date" type="date" value={form.bid_due_date} onChange={e => setForm(f => ({ ...f, bid_due_date: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Bid Due Time</label>
                  <input type="time" value={form.bid_due_time} onChange={e => setForm(f => ({ ...f, bid_due_time: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, paddingBottom: 4 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
                    <input type="checkbox" checked={form.prevailing_wage} onChange={e => setForm(f => ({ ...f, prevailing_wage: e.target.checked }))} /> Prevailing Wage
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
                    <input type="checkbox" checked={form.bond_required} onChange={e => setForm(f => ({ ...f, bond_required: e.target.checked }))} /> Bond Req'd
                  </label>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Scope Description</label>
                <textarea data-testid="bid-scope" value={form.scope_description} onChange={e => setForm(f => ({ ...f, scope_description: e.target.value }))}
                  rows={3} placeholder="Brief scope description..." style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <button data-testid="submit-create-bid" onClick={handleCreate}
                disabled={!form.account_id || !form.project_name.trim()}
                style={{ width: '100%', padding: '12px', background: form.account_id && form.project_name.trim() ? '#c0392b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
                Create Bid Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
